import createMergeFn from '@fastify/deepmerge';
import useMatchMedia from '../../../../use-match-media-hook';
const deepMerge = createMergeFn({ all: true, symbols: false });
export const mergeStyles = (styles) => deepMerge(...styles);
/**
 * @param value Map of breakpoints to values, e.g. `{ 600: 'md', 960: 'lg' }`
 * @param styles Function that receives the value and returns a style object
 * @returns Style object with styles for each breakpoint
 */
export const getResponsiveStyles = (value, styles) => isSingularValue(value)
    ? styles(value)
    : Object.fromEntries(Object.entries(value).map(([breakpoint, value]) => [
        `@media (min-width: ${breakpoint}px)`,
        styles(value),
    ]));
/**
 * @param value Map of breakpoints to values, e.g. `{ 600: 'md', 960: 'lg' }`
 * @param mapper Function that receives the value and returns a mapped value
 * @returns Map of breakpoints to mapped values
 */
export const mapResponsiveValue = (value, mapper) => isSingularValue(value)
    ? mapper(value)
    : Object.fromEntries(Object.entries(value).map(([breakpoint, value]) => [
        breakpoint,
        mapper(value),
    ]));
export const isBreakpointsMap = (value) => typeof value === 'object' && value != null && !Array.isArray(value);
export const isSingularValue = (value) => !isBreakpointsMap(value);
export function combineResponsiveValues(...values) {
    const singularValues = values.filter(isSingularValue);
    if (singularValues.length === values.length) {
        return singularValues;
    }
    const breakpoints = Array.from(new Set(values
        .flatMap((value) => isBreakpointsMap(value)
        ? Object.keys(value).map((b) => parseInt(b, 10))
        : [])
        .sort((a, b) => a - b)));
    const entries = [];
    breakpoints.forEach((b, i) => {
        entries.push(values.map((v, k) => {
            if (isBreakpointsMap(v)) {
                if (b in v) {
                    return v[b];
                }
                if (i - 1 in entries) {
                    if (k in entries[i - 1]) {
                        return entries[i - 1][k];
                    }
                }
                throw new Error(`No explicit value has been provided for breakpoint ${b} or lower in ${JSON.stringify(v)}`);
            }
            return v;
        }));
    });
    return Object.fromEntries(breakpoints.map((breakpoint, index) => [breakpoint, entries[index]]));
}
export function useCurrentResponsiveValue(value) {
    const isResponsive = isBreakpointsMap(value);
    const matches = useMatchMedia(isResponsive
        ? Object.keys(value).map((breakpoint) => `(min-width: ${breakpoint}px)`)
        : []);
    return isResponsive
        ? Object.values(value).at(matches.lastIndexOf(true))
        : value;
}
//# sourceMappingURL=responsive.js.map